// src/components/AuditorDeductionDashboard.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tooltip,
  Avatar,
  alpha,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  Comment as CommentIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from 'recharts';

import Sidebar from '../../components/sidebars/Sidebar_auditor';
import TopNav from '../../components/top_bars/TopNav_auditor';

const statusColors = {
  pending: { bg: '#fff3e0', text: '#ff9800', label: 'Pending' },
  approved: { bg: '#e8f5e9', text: '#4caf50', label: 'Approved' },
  rejected: { bg: '#ffebee', text: '#f44336', label: 'Rejected' },
};

const COLORS = ['#ff9800', '#4caf50', '#f44336', '#2196f3', '#9c27b0'];

const AuditorDeductionDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [allDeductions, setAllDeductions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedDeduction, setSelectedDeduction] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [departments, setDepartments] = useState([]);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  useEffect(() => {
    fetchAllData();
    fetchDepartments();
  }, []);

 const fetchAllData = async () => {
  setLoading(true);
  try {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
    
    // Fetch deductions using the salary_deductions API
    const allRes = await fetch(`${baseUrl}/salary_deductions.php`);
    const allResult = await allRes.json();
    
    if (allResult.success) {
      // Process and sanitize deduction data
      const safeData = (allResult.data || []).map(item => ({
        id: item.id,
        phone: item.phone || 'N/A',
        amount: parseFloat(item.amount) || 0,
        remark: item.remark || 'No reason provided',
        deduction_status: item.deduction_status !== undefined ? item.deduction_status : 0,
        approved_by: item.approved_by || null,
        approved_date: item.approved_date || null,
        rejection_reason: item.rejection_reason || null,
        date_created: item.date_created || new Date().toISOString(),
        date_updated: item.date_updated || null,
        full_name: item.full_name || 'Unknown Staff',
        department: item.department || 'Not Assigned',
        email: item.email || 'N/A',
        current_salary: parseFloat(item.current_salary) || 0,
        status_text: item.status_text || (item.deduction_status === 0 ? 'Pending' : item.deduction_status === 1 ? 'Approved' : 'Rejected'),
        status_color: item.status_color || (item.deduction_status === 0 ? '#ff9800' : item.deduction_status === 1 ? '#4caf50' : '#f44336')
      }));
      
      setAllDeductions(safeData);
      
      // Calculate statistics from the actual data
      const pendingCount = safeData.filter(d => d.deduction_status === 0).length;
      const approvedCount = safeData.filter(d => d.deduction_status === 1).length;
      const rejectedCount = safeData.filter(d => d.deduction_status === 2).length;
      
      const pendingAmount = safeData.filter(d => d.deduction_status === 0).reduce((sum, d) => sum + d.amount, 0);
      const approvedAmount = safeData.filter(d => d.deduction_status === 1).reduce((sum, d) => sum + d.amount, 0);
      const rejectedAmount = safeData.filter(d => d.deduction_status === 2).reduce((sum, d) => sum + d.amount, 0);
      
      setStats({
        total: safeData.length,
        pending: pendingCount,
        approved: approvedCount,
        rejected: rejectedCount,
        total_pending_amount: pendingAmount,
        total_approved_amount: approvedAmount,
        total_rejected_amount: rejectedAmount
      });
    } else {
      setError(allResult.message || 'Failed to load data');
    }
    
  } catch (err) {
    console.error('Error fetching data:', err);
    setError('Failed to load data');
    setSnackbar({ open: true, message: 'Failed to load deduction data', severity: 'error' });
  } finally {
    setLoading(false);
  }
};
  const fetchDepartments = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/get_depts_list.php`);
      const result = await response.json();
      if (result.success) {
        setDepartments(result.data);
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₦0.00';
    const num = parseFloat(amount);
    if (isNaN(num)) return '₦0.00';
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(num);
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    const val = parseFloat(num);
    if (isNaN(val)) return '0';
    return new Intl.NumberFormat('en-NG').format(Math.round(val));
  };

  // Filter deductions with safe null checks
  const getFilteredDeductions = () => {
    let filtered = [...allDeductions];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(d => 
        (d.full_name || '').toLowerCase().includes(term) ||
        (d.department || '').toLowerCase().includes(term) ||
        (d.phone || '').includes(term) ||
        (d.remark || '').toLowerCase().includes(term)
      );
    }
    
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(d => d.department === departmentFilter);
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(d => {
        if (statusFilter === 'pending') return d.deduction_status === 0;
        if (statusFilter === 'approved') return d.deduction_status === 1;
        if (statusFilter === 'rejected') return d.deduction_status === 2;
        return true;
      });
    }
    
    if (dateRange.start) {
      filtered = filtered.filter(d => new Date(d.date_created) >= new Date(dateRange.start));
    }
    if (dateRange.end) {
      filtered = filtered.filter(d => new Date(d.date_created) <= new Date(dateRange.end));
    }
    
    return filtered;
  };

  const exportToCSV = () => {
    const filtered = getFilteredDeductions();
    const headers = ['Staff Name', 'Department', 'Phone', 'Amount', 'Reason', 'Status', 'Request Date', 'Approved By', 'Approved Date'];
    const csvData = filtered.map(d => [
      d.full_name || 'N/A',
      d.department || 'N/A',
      d.phone || 'N/A',
      d.amount || 0,
      d.remark || '',
      d.status_text || 'Unknown',
      d.date_created ? new Date(d.date_created).toLocaleDateString() : 'N/A',
      d.approved_by || 'N/A',
      d.approved_date ? new Date(d.approved_date).toLocaleDateString() : 'N/A'
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `deductions_audit_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const printReport = () => {
    window.print();
  };

  const filteredDeductions = getFilteredDeductions();
  const uniqueDepartments = ['all', ...new Set(allDeductions.map(d => d.department).filter(Boolean))];
  
  // Department wise deduction stats with safe calculations
  const deptDeductionStats = () => {
    const deptMap = new Map();
    allDeductions.forEach(d => {
      const dept = d.department || 'Not Assigned';
      if (!deptMap.has(dept)) {
        deptMap.set(dept, { total: 0, count: 0, pending: 0, approved: 0 });
      }
      const statsData = deptMap.get(dept);
      const amount = parseFloat(d.amount) || 0;
      statsData.total += amount;
      statsData.count++;
      if (d.deduction_status === 0) statsData.pending += amount;
      if (d.deduction_status === 1) statsData.approved += amount;
    });
    return Array.from(deptMap.entries()).map(([department, data]) => ({ department, ...data }));
  };

  // Monthly trend data with safe calculations
  const monthlyTrendData = () => {
    const monthMap = new Map();
    allDeductions.forEach(d => {
      if (!d.date_created) return;
      const date = new Date(d.date_created);
      const month = date.toLocaleString('default', { month: 'short', year: 'numeric' });
      if (!monthMap.has(month)) {
        monthMap.set(month, { month, total: 0, count: 0, approved: 0, rejected: 0 });
      }
      const data = monthMap.get(month);
      const amount = parseFloat(d.amount) || 0;
      data.total += amount;
      data.count++;
      if (d.deduction_status === 1) data.approved += amount;
      if (d.deduction_status === 2) data.rejected += amount;
    });
    return Array.from(monthMap.values()).slice(-12);
  };

  const pieChartData = [
    { name: 'Pending', value: stats?.pending || 0, color: '#ff9800' },
    { name: 'Approved', value: stats?.approved || 0, color: '#4caf50' },
    { name: 'Rejected', value: stats?.rejected || 0, color: '#f44336' },
  ].filter(item => item.value > 0);

  const deptStats = deptDeductionStats();
  const monthlyData = monthlyTrendData();

  const StatCard = ({ title, value, subtitle, color, icon: Icon }) => (
    <Card sx={{ borderRadius: 3, height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="body2" color="textSecondary">{title}</Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>{value}</Typography>
            {subtitle && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: alpha(color, 0.1), color: color, width: 48, height: 48 }}>
            <Icon />
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const totalDeductions = allDeductions.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
  const approvedTotal = allDeductions.filter(d => d.deduction_status === 1).reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
  const pendingTotal = allDeductions.filter(d => d.deduction_status === 0).reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
  const rejectedTotal = allDeductions.filter(d => d.deduction_status === 2).reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);

  if (loading && allDeductions.length === 0) {
    return (
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <Box sx={{ flexGrow: 1, p: 3, ml: isMobile ? 0 : sidebarOpen ? 30 : 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading deduction data...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f5f5f5' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box sx={{ flexGrow: 1, p: 3, ml: isMobile ? 0 : sidebarOpen ? 30 : 5, maxWidth: '1600px', mx: 'auto', width: '100%', overflow: 'auto', height: '100vh' }}>
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            localStorage.removeItem('phoneNumber');
          }}
          onNotificationClick={() => alert('You have new notifications!')}
          toggleSidebar={toggleSidebar}
        />

        {/* Header */}
        <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, background: `linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)`, color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold">Auditor Dashboard - Salary Deductions</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>Comprehensive audit and analysis of all salary deductions</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="contained" startIcon={<PrintIcon />} onClick={printReport} sx={{ bgcolor: 'white', color: '#1565c0', '&:hover': { bgcolor: '#f5f5f5' } }}>
                Print Report
              </Button>
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={exportToCSV} sx={{ bgcolor: 'white', color: '#1565c0', '&:hover': { bgcolor: '#f5f5f5' } }}>
                Export CSV
              </Button>
              <Button variant="contained" startIcon={<RefreshIcon />} onClick={fetchAllData} sx={{ bgcolor: 'white', color: '#1565c0', '&:hover': { bgcolor: '#f5f5f5' } }}>
                Refresh
              </Button>
            </Box>
          </Box>
        </Paper>

      

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search by name, department, phone, or reason..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flex: 2, minWidth: 200 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Department</InputLabel>
              <Select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)} label="Department">
                <MenuItem value="all">All Departments</MenuItem>
                {uniqueDepartments.filter(d => d !== 'all').map((dept, idx) => (
                  <MenuItem key={idx} value={dept}>{dept}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              sx={{ width: 150 }}
            />
            <TextField
              size="small"
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              sx={{ width: 150 }}
            />
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => {
              setSearchTerm('');
              setDepartmentFilter('all');
              setStatusFilter('all');
              setDateRange({ start: '', end: '' });
              fetchAllData();
            }}>
              Reset
            </Button>
          </Box>
        </Paper>

        {/* Deductions Table */}
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Deductions Audit Register</Typography>
            <Chip label={`${filteredDeductions.length} records found`} size="small" sx={{ bgcolor: '#1565c0', color: 'white' }} />
          </Box>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Staff Member</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Request Date</TableCell>
                  <TableCell>Approved By</TableCell>
                  <TableCell align="center">View</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDeductions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                      <Typography variant="body1" color="textSecondary">No deduction records found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDeductions.map((deduction, index) => {
                    const statusKey = deduction.deduction_status === 0 ? 'pending' : deduction.deduction_status === 1 ? 'approved' : 'rejected';
                    return (
                      <TableRow key={deduction.id} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: statusColors[statusKey]?.bg || '#e0e0e0' }}>
                              <PersonIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">{deduction.full_name || 'Unknown'}</Typography>
                              <Typography variant="caption" color="textSecondary">{deduction.phone || 'No phone'}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={deduction.department || 'N/A'} variant="outlined" />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" color={deduction.deduction_status === 1 ? '#4caf50' : '#f44336'}>
                            {deduction.deduction_status === 1 ? '-' : ''}{formatCurrency(deduction.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={deduction.remark || 'No reason provided'}>
                            <Chip 
                              size="small" 
                              label={(deduction.remark || 'No reason').substring(0, 30)}
                              variant="outlined"
                              icon={<CommentIcon sx={{ fontSize: 14 }} />}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={deduction.status_text || 'Unknown'} 
                            size="small"
                            sx={{ bgcolor: alpha(deduction.status_color || '#757575', 0.1), color: deduction.status_color || '#757575', fontWeight: 'medium' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {deduction.date_created ? new Date(deduction.date_created).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {deduction.approved_by || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small" 
                              onClick={() => { setSelectedDeduction(deduction); setViewDialogOpen(true); }}
                              sx={{ color: '#1565c0' }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Summary Footer */}
        <Paper sx={{ p: 2, mt: 3, borderRadius: 3, bgcolor: '#f5f5f5' }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="textSecondary">Total Records</Typography>
              <Typography variant="body1" fontWeight="bold">{filteredDeductions.length}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="textSecondary">Total Amount</Typography>
              <Typography variant="body1" fontWeight="bold" color="#1565c0">
                {formatCurrency(filteredDeductions.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0))}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="textSecondary">Average Deduction</Typography>
              <Typography variant="body1" fontWeight="bold">
                {filteredDeductions.length > 0 ? formatCurrency(filteredDeductions.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0) / filteredDeductions.length) : '₦0.00'}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="textSecondary">Audit Period</Typography>
              <Typography variant="body1" fontWeight="bold">
                {dateRange.start ? new Date(dateRange.start).toLocaleDateString() : 'All'} - {dateRange.end ? new Date(dateRange.end).toLocaleDateString() : 'Present'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <footer style={{ textAlign: 'center', padding: '20px', marginTop: 'auto' }}>
          <Typography variant="body2" color="textSecondary">© {new Date().getFullYear()} Elegantè IT - Auditor Dashboard</Typography>
        </footer>
      </Box>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        {selectedDeduction && (
          <>
            <DialogTitle sx={{ bgcolor: '#1565c0', color: 'white' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Deduction Details - Audit View</Typography>
                <IconButton onClick={() => setViewDialogOpen(false)} sx={{ color: 'white' }}><CancelIcon /></IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Staff Information</Typography>
                    <Stack spacing={1.5} sx={{ mt: 2 }}>
                      <Box><strong>Name:</strong> {selectedDeduction.full_name || 'Unknown'}</Box>
                      <Box><strong>Department:</strong> {selectedDeduction.department || 'Not Assigned'}</Box>
                      <Box><strong>Phone:</strong> {selectedDeduction.phone || 'N/A'}</Box>
                      <Box><strong>Email:</strong> {selectedDeduction.email || 'N/A'}</Box>
                      <Box><strong>Current Salary:</strong> {formatCurrency(selectedDeduction.current_salary)}</Box>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Deduction Information</Typography>
                    <Stack spacing={1.5} sx={{ mt: 2 }}>
                      <Box><strong>Amount Deducted:</strong> <span style={{ color: '#f44336', fontWeight: 'bold' }}>{formatCurrency(selectedDeduction.amount)}</span></Box>
                      <Box><strong>Reason:</strong> {selectedDeduction.remark || 'No reason provided'}</Box>
                      <Box><strong>Status:</strong> <Chip label={selectedDeduction.status_text} size="small" sx={{ bgcolor: alpha(selectedDeduction.status_color, 0.1), color: selectedDeduction.status_color }} /></Box>
                      <Box><strong>Request Date:</strong> {selectedDeduction.date_created ? new Date(selectedDeduction.date_created).toLocaleString() : 'N/A'}</Box>
                      {selectedDeduction.approved_by && (
                        <>
                          <Box><strong>Approved By:</strong> {selectedDeduction.approved_by}</Box>
                          <Box><strong>Approved Date:</strong> {selectedDeduction.approved_date ? new Date(selectedDeduction.approved_date).toLocaleString() : 'N/A'}</Box>
                        </>
                      )}
                      {selectedDeduction.rejection_reason && (
                        <Box><strong>Rejection Reason:</strong> <span style={{ color: '#f44336' }}>{selectedDeduction.rejection_reason}</span></Box>
                      )}
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewDialogOpen(false)} variant="outlined">Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AuditorDeductionDashboard;